import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const THEMES = ['minimal', 'mystic', 'morandi'] as const;
type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'tarot.theme';

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'minimal';
  const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (stored && THEMES.includes(stored)) return stored;
  const docTheme = document.documentElement.getAttribute('data-theme') as Theme | null;
  return docTheme && THEMES.includes(docTheme) ? docTheme : 'minimal';
}

/**
 * 切換 mystic / morandi / minimal — demo 用。實際買斷部署時 theme 由 env 鎖死。
 * 寫入 <html data-theme="..."> 並持久化到 localStorage。
 */
export default function ThemeSwitcher() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="flex items-center gap-1 rounded-full border border-border bg-surface/80 p-1 text-xs backdrop-blur"
    >
      {THEMES.map((opt) => {
        const active = theme === opt;
        return (
          <button
            key={opt}
            role="radio"
            aria-checked={active}
            onClick={() => setTheme(opt)}
            className={[
              'rounded-full px-3 py-1 transition-colors',
              active ? 'bg-fg text-bg' : 'text-muted hover:text-fg',
            ].join(' ')}
          >
            {t(`common.themes.${opt}`)}
          </button>
        );
      })}
    </div>
  );
}
